import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { toast } from 'sonner';
import axios from 'axios';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { 
  Camera, Zap, History, Moon, Sun, Search, Loader2,
  Calendar, Calculator, Package, ChevronRight, Check, X, Infinity, Crown, Sparkles, Shield, ArrowRight, Gift, Star, Clock
} from 'lucide-react';
import { FlipValueLogoBox } from '../components/FlipValueLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature flag for scan functionality - set to false for production launch
const ENABLE_SCAN = process.env.REACT_APP_ENABLE_SCAN === 'true';

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, token } = useAuth();
  const { actionsRemaining, recordAction } = useGuest();
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchCondition, setSearchCondition] = useState('New');
  const [showConfirmResults, setShowConfirmResults] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [showCardSuggestion, setShowCardSuggestion] = useState(false);
  
  const isGuest = !user;
  const isFreeOrBasic = !user || !user.subscription || ['free', 'basic'].includes(user.subscription?.tier);

  // Check if query is a card search
  const isCardSearch = (query) => {
    const q = query.toLowerCase();
    const gradingKeywords = ['psa', 'bgs', 'cgc', 'sgc', 'graded'];
    const cardKeywords = ['pokemon', 'yu-gi-oh', 'yugioh', 'magic', 'mtg', 'baseball', 'football', 'basketball', 'hockey', 'card', 'topps', 'panini', 'fleer', 'upper deck'];
    return gradingKeywords.some(kw => q.includes(kw)) || cardKeywords.some(kw => q.includes(kw));
  };

  // Handle manual condition change
  const handleConditionChange = (newCondition) => {
    setSearchCondition(newCondition);
    setShowCardSuggestion(false);
  };

  // Track search usage
  const trackSearchUsage = async () => {
    if (isGuest) {
      recordAction();
    } else if (token) {
      try {
        await axios.post(
          `${API}/usage/track?usage_type=scans_used`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to track search usage:', error);
      }
    }
  };

  const handleGetStarted = () => {
    navigate('/dashboard');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.post(`${API}/search`, { 
        query: searchQuery.trim(),
        condition: searchCondition
      });
      // Show results for confirmation
      setSearchResults(response.data);
      setShowConfirmResults(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };
  
  const confirmResults = async () => {
    if (searchResults) {
      await trackSearchUsage();
      // Store search data in sessionStorage for guest users
      sessionStorage.setItem(`search_${searchResults.id}`, JSON.stringify(searchResults));
      navigate(`/search/${searchResults.id}`);
    }
    setShowConfirmResults(false);
  };

  const toolkitFeatures = [
    {
      icon: Calculator,
      title: 'Profit Calculator',
      description: 'Instant break-even and profit calculations',
      path: '/calculator',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    },
    {
      icon: Package,
      title: 'Personal Inventory',
      description: 'Track items, get price alerts, share with buyers',
      path: '/inventory',
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10'
    },
    {
      icon: Calendar,
      title: 'Release Schedule',
      description: 'Calendar, pre-order alerts, stay ahead of drops',
      path: '/releases',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      icon: History,
      title: 'Search History',
      description: 'Review past valuations',
      path: '/history',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    }
  ];

  // Billing interval state for pricing toggle
  const [billingInterval, setBillingInterval] = useState('monthly');
  
  // Lifetime Pro state
  const [lifetimeProStatus, setLifetimeProStatus] = useState(null);
  
  useEffect(() => {
    // Fetch Lifetime Pro status
    const fetchLifetimeStatus = async () => {
      try {
        console.log('Fetching Lifetime Pro status...');
        const response = await axios.get(`${API}/billing/lifetime-pro/status`);
        console.log('Lifetime Pro status:', response.data);
        setLifetimeProStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch Lifetime Pro status:', error);
        // Set a default to show the banner even if API fails
        setLifetimeProStatus({
          available: true,
          remaining: 48,
          max: 50,
          price: 249
        });
      }
    };
    fetchLifetimeStatus();
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden flex flex-col">
      {/* Noise texture for dark mode */}
      <div className="dark:noise-texture absolute inset-0" />
      
      {/* Header */}
      <header className="relative z-10 px-4 py-4 flex items-center justify-between max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-2">
          <NavigationMenu />
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <FlipValueLogoBox size="lg" withGlow={true} />
            <span className="text-xl font-bold tracking-tight">FlipValue</span>
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <UserMenu />
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-0 px-4 pt-6 pb-8 max-w-7xl mx-auto flex-1">
        <div className="text-center max-w-3xl mx-auto">
          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4 leading-[1.1]">
            Search. Track. Profit. <span className="inline-block">⚡</span>
          </h1>

          {/* Subheadline */}
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-8">
            Instant eBay profit calculations, personal inventory tracking, price alerts, and release schedules — built for resellers.
          </p>

          {/* Search Bar - Made More Prominent */}
          <div className="max-w-xl mx-auto mb-6">
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!searchQuery.trim()) return;
              
              // Check if searching for cards and suggest Used condition
              if (isCardSearch(searchQuery) && searchCondition === 'New') {
                setShowCardSuggestion(true);
                return;
              }
              
              handleSearch(e);
            }} className="space-y-3">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search any item (e.g., PSA 10 Charizard, Nike Dunk Low)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowCardSuggestion(false);
                  }}
                  onFocus={(e) => {
                    // Mobile keyboard fix - scroll input into view
                    setTimeout(() => {
                      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 300);
                  }}
                  className="pl-12 py-6 text-base rounded-xl border-2 focus:border-primary"
                  data-testid="search-input"
                />
              </div>
              
              {/* Card Suggestion Banner */}
              {showCardSuggestion && (
                <div className="flex items-center justify-between gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    💡 Trading cards are typically sold as Used. Search with Used condition?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowCardSuggestion(false);
                        // Execute search with New condition
                        handleSearch({ preventDefault: () => {} });
                      }}
                      className="text-xs"
                    >
                      Keep New
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={async () => {
                        setShowCardSuggestion(false);
                        // Execute search directly with Used condition
                        setSearching(true);
                        try {
                          const response = await axios.post(`${API}/search`, { 
                            query: searchQuery.trim(),
                            condition: 'Used'
                          });
                          setSearchCondition('Used');
                          setSearchResults(response.data);
                          setShowConfirmResults(true);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Search failed');
                        } finally {
                          setSearching(false);
                        }
                      }}
                      className="text-xs"
                    >
                      Used
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Condition Toggle + Search Button Row */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1 bg-secondary rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => handleConditionChange('New')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                      searchCondition === 'New' 
                        ? 'bg-background text-foreground shadow-sm' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    New
                  </button>
                  <button
                    type="button"
                    onClick={() => handleConditionChange('Used')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                      searchCondition === 'Used' 
                        ? 'bg-background text-foreground shadow-sm' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Used
                  </button>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={searching || !searchQuery.trim()}
                  className="px-8 py-5"
                >
                  {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Free searches info - only for guests/free users */}
          {!user && (
            <p className="text-xs text-muted-foreground mb-6">
              No account required • {actionsRemaining} free searches
            </p>
          )}

          {/* Feature Bullets */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto mb-6 text-left">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="text-base">🔍</span>
              <span>Search any item for real market data</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="text-base">💰</span>
              <span>Instant break-even and profit calculations</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="text-base">📦</span>
              <span>Track personal inventory with price alerts</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="text-base">🔔</span>
              <span>Plan releases and pre-orders</span>
            </div>
          </div>

          {/* Sign Up Free Button */}
          {!user && (
            <Button 
              size="lg"
              className="px-8 py-6 text-base font-semibold rounded-xl"
              onClick={() => navigate('/auth?mode=signup')}
              data-testid="signup-free-btn"
            >
              Sign Up Free
            </Button>
          )}

          {/* Scan Button - Only show if enabled */}
          {ENABLE_SCAN && (
            <>
              <div className="flex items-center gap-3 max-w-lg mx-auto mb-3 mt-4">
                <div className="flex-1 h-px bg-border"></div>
                <span className="text-xs text-muted-foreground">or upload a photo</span>
                <div className="flex-1 h-px bg-border"></div>
              </div>

              <div className="flex flex-col items-center justify-center">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto rounded-full text-base font-semibold px-8 py-5"
                  onClick={handleGetStarted}
                  data-testid="get-started-btn"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Scan with Camera / Upload Photo
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Tools Section */}
        <div className="mt-12 max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Essential Tools for Resellers</h2>
            <p className="text-sm text-muted-foreground">Plan drops, calculate profits, track your inventory</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {toolkitFeatures.map((feature) => (
              <ToolkitCard
                key={feature.path}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                color={feature.color}
                bgColor={feature.bgColor}
                onClick={() => navigate(feature.path)}
              />
            ))}
          </div>
        </div>

        {/* Pricing Section */}
        <div className="mt-12 max-w-4xl mx-auto" id="pricing">
          <div className="text-center mb-4">
            <h2 className="text-lg sm:text-xl font-bold mb-1">Simple, Transparent Pricing</h2>
            <p className="text-xs text-muted-foreground">Start free, upgrade when you need more</p>
          </div>

          {/* Free Plan - Compact Card */}
          <div className="bg-card rounded-lg border border-border p-3 mb-4 max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-secondary text-muted-foreground">
                  <Shield className="w-4 h-4" />
                </div>
                <div>
                  <span className="font-bold text-sm">Free</span>
                  <span className="ml-2 text-lg font-bold">$0</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                <span className="flex items-center gap-1"><Check className="w-2.5 h-2.5 text-primary" />50 lifetime actions</span>
                <span className="flex items-center gap-1"><Check className="w-2.5 h-2.5 text-primary" />10 inventory</span>
                <span className="flex items-center gap-1"><Check className="w-2.5 h-2.5 text-primary" />Basic sharing</span>
                <span className="flex items-center gap-1"><Check className="w-2.5 h-2.5 text-primary" />eBay only</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="whitespace-nowrap h-8 text-xs"
                onClick={() => navigate('/auth?mode=signup')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>

          {/* Billing Toggle - Compact */}
          <div className="flex justify-center mb-3">
            <div className="inline-flex items-center bg-secondary/80 rounded-full p-0.5 text-xs border border-border">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-3 py-1.5 rounded-full font-semibold transition-all ${
                  billingInterval === 'monthly' 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('annual')}
                className={`px-3 py-1.5 rounded-full font-semibold transition-all flex items-center gap-1 ${
                  billingInterval === 'annual' 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Annual
                <span className="px-1 py-0.5 bg-emerald-500 text-white text-[8px] font-bold rounded">
                  -23%
                </span>
              </button>
            </div>
          </div>

          {/* Basic & Pro Cards - Compact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {/* Basic Plan */}
            <div className="relative bg-card rounded-lg border border-border p-3">
              <div className="absolute -top-2 right-3 px-2 py-0.5 bg-teal-600 text-white text-[9px] font-bold rounded-full">
                3-day trial
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-500">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Basic</h3>
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xl font-bold">${billingInterval === 'annual' ? '49' : '4.99'}</span>
                    <span className="text-muted-foreground text-xs">/{billingInterval === 'annual' ? 'yr' : 'mo'}</span>
                    {billingInterval === 'annual' && <span className="ml-1 px-1.5 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded">-17%</span>}
                  </div>
                </div>
              </div>
              <ul className="space-y-1 mb-3 text-xs">
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" />Unlimited searches & calculations</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" />1 platform • Unlimited inventory</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" />Advanced sharing</li>
                <li className="flex items-center gap-1.5 text-muted-foreground"><X className="w-3 h-3 text-red-400" />No alerts or releases</li>
              </ul>
              <Button size="sm" className="w-full h-8 bg-blue-500 hover:bg-blue-600 text-white text-xs" onClick={() => navigate('/pricing')}>
                Start Free Trial
              </Button>
            </div>

            {/* Pro Plan */}
            <div className="relative bg-card rounded-lg border-2 border-primary p-3">
              <div className="absolute -top-2 left-3 px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-bold rounded-full">
                POPULAR
              </div>
              <div className="absolute -top-2 right-3 px-2 py-0.5 bg-emerald-700 text-white text-[9px] font-bold rounded-full">
                7-day trial
              </div>
              <div className="flex items-center gap-2 mb-2 mt-1">
                <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                  <Crown className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Pro</h3>
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xl font-bold">${billingInterval === 'annual' ? '99' : '9.99'}</span>
                    <span className="text-muted-foreground text-xs">/{billingInterval === 'annual' ? 'yr' : 'mo'}</span>
                    {billingInterval === 'annual' && <span className="ml-1 px-1.5 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded">-23%</span>}
                  </div>
                </div>
              </div>
              <ul className="space-y-1 mb-3 text-xs">
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" />Unlimited everything</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" />All platforms • Alerts • Sharing</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-primary" />Releases + CSV exports</li>
              </ul>
              <Button size="sm" className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => navigate('/pricing')}>
                Start Free Trial
              </Button>
            </div>
          </div>

          {/* Lifetime Pro - Launch Special Banner */}
          {lifetimeProStatus?.available && (
            <div className="mt-4 max-w-2xl mx-auto">
              <div 
                onClick={() => navigate('/pricing')}
                className="relative bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 rounded-lg border-2 border-amber-400 dark:border-amber-600 p-3 cursor-pointer hover:shadow-lg transition-all group"
              >
                {/* Launch Badge */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                  <div className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-full shadow flex items-center gap-1.5 whitespace-nowrap">
                    <Star className="w-3 h-3 fill-white" />
                    LAUNCH SPECIAL
                    <Star className="w-3 h-3 fill-white" />
                  </div>
                </div>
                
                {/* Remaining Badge */}
                <div className="absolute -top-2 -right-2">
                  <div className="px-2 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full animate-pulse">
                    {lifetimeProStatus.remaining} left!
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white">
                      <Gift className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-amber-900 dark:text-amber-100">Lifetime Pro</h3>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-amber-600 dark:text-amber-400">${lifetimeProStatus.price}</span>
                        <span className="text-amber-700 dark:text-amber-300 text-xs">one-time</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-amber-800 dark:text-amber-200">
                    <span className="flex items-center gap-1"><Check className="w-2.5 h-2.5 text-amber-600" />Full Pro forever</span>
                    <span className="flex items-center gap-1"><Check className="w-2.5 h-2.5 text-amber-600" />All future features</span>
                    <span className="flex items-center gap-1"><Check className="w-2.5 h-2.5 text-amber-600" />No recurring fees</span>
                  </div>
                  
                  <Button 
                    size="sm" 
                    className="h-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-bold whitespace-nowrap group-hover:scale-105 transition-transform"
                  >
                    <Gift className="w-3.5 h-3.5 mr-1" />
                    Get Lifetime — ${lifetimeProStatus.price}
                  </Button>
                </div>
                
                <p className="text-[9px] text-amber-600 dark:text-amber-400 text-center mt-2 flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  Limited to {lifetimeProStatus.max} early supporters • Ends Jan 31, 2026
                </p>
              </div>
            </div>
          )}

          <div className="text-center mt-3">
            <button onClick={() => navigate('/pricing')} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
              Compare all features <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>

      {/* Search Results Confirmation Dialog */}
      <Dialog open={showConfirmResults} onOpenChange={setShowConfirmResults}>
        <DialogContent className="sm:max-w-sm p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Results for &ldquo;{searchQuery}&rdquo;</DialogTitle>
            <p className="text-[10px] text-muted-foreground">Add brand, model, size for better results</p>
          </DialogHeader>
          <div className="space-y-2">
            {searchResults?.comps?.length > 0 ? (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {searchResults.comps.slice(0, 5).map((comp, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-1.5 rounded border border-border bg-card">
                    {comp.image_url && (
                      <img src={comp.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] line-clamp-2">{comp.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No results found</p>
            )}
            
            <div className="bg-primary/10 rounded-lg p-2">
              <p className="text-[10px] text-muted-foreground">Estimated Value</p>
              <p className={`text-xl font-bold text-primary ${isFreeOrBasic ? 'blur-sm select-none' : ''}`}>
                ${searchResults?.average_price?.toFixed(2) || '0.00'}
              </p>
              {isFreeOrBasic && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {isGuest ? 'Sign up to see values' : 'Upgrade to see values'}
                </p>
              )}
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowConfirmResults(false)} className="flex-1" size="sm">
                Try Again
              </Button>
              <Button onClick={confirmResults} disabled={!searchResults?.comps?.length} className="flex-1" size="sm">
                Looks Good
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
}

function ToolkitCard({ icon: Icon, title, description, color, bgColor, onClick }) {
  return (
    <button
      onClick={onClick}
      className="p-5 rounded-2xl border-2 border-border bg-card hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 hover:scale-[1.02] transition-all duration-200 text-left group cursor-pointer"
    >
      <div className={`w-12 h-12 rounded-xl ${bgColor} flex items-center justify-center ${color} mb-3 group-hover:scale-110 transition-transform duration-200`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-bold text-sm mb-1 flex items-center gap-1.5">
        {title}
        <ChevronRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 text-primary" />
      </h3>
      <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
    </button>
  );
}
